import { PARQ_QUESTIONS } from '../constants/parqQuestions';
import {
  PUBLIC_ANAMNESE_FIELD_LABELS as L,
  PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS,
  PUBLIC_MAIN_GOAL_OPTIONS,
  PUBLIC_ONLINE_COURSE_OPTIONS,
  PUBLIC_REFERRAL_SOURCE_OPTIONS,
  PUBLIC_TRAINING_LEVEL_OPTIONS,
  PUBLIC_TRAINING_LOCATION_OPTIONS,
  PUBLIC_WEEKLY_AVAILABILITY_OPTIONS,
} from '../pages/Anamnesis/AnamnesisRegister/publicAnamnese.constants';
import type { Anamnesis } from '../types/anamnesis';

export type StudentAnswerRow = {
  key: string;
  label: string;
  value: string | boolean | null;
};

const textValue = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed || null;
};

const optionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined,
): string | null => {
  const raw = textValue(value);
  if (!raw) {
    return null;
  }

  return options.find((option) => option.value === raw)?.label ?? raw;
};

const pushText = (
  rows: StudentAnswerRow[],
  key: string,
  label: string,
  value: string | null | undefined,
) => {
  rows.push({ key, label, value: textValue(value) });
};

const pushBoolean = (
  rows: StudentAnswerRow[],
  key: string,
  label: string,
  value: boolean | null | undefined,
) => {
  rows.push({ key, label, value: value ?? null });
};

const pushSelect = (
  rows: StudentAnswerRow[],
  key: string,
  label: string,
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined,
) => {
  rows.push({ key, label, value: optionLabel(options, value) });
};

export function buildAnamnesisAnswerRows(anamnesis: Anamnesis | null): StudentAnswerRow[] {
  if (!anamnesis) {
    return [];
  }

  const rows: StudentAnswerRow[] = [];

  pushText(rows, 'profession', L.profession, anamnesis.profession);
  pushText(rows, 'previousOnlineConsultancy', L.previousOnlineConsultancy, anamnesis.previous_online_consultancy);
  pushSelect(rows, 'onlineCourse', L.onlineCourse, PUBLIC_ONLINE_COURSE_OPTIONS, anamnesis.online_course);
  pushText(rows, 'otherCourse', 'Qual curso?', anamnesis.other_course);
  pushText(rows, 'courseFeedback', L.courseFeedback, anamnesis.course_feedback);
  pushSelect(rows, 'trainingLevel', L.trainingLevel, PUBLIC_TRAINING_LEVEL_OPTIONS, anamnesis.training_level);
  pushText(rows, 'trainingLevelOther', 'Descreva o nível de treinamento', anamnesis.training_level_other);
  pushSelect(rows, 'trainingLocation', L.trainingLocation, PUBLIC_TRAINING_LOCATION_OPTIONS, anamnesis.training_location);
  pushText(rows, 'fullGymName', L.fullGymName, anamnesis.full_gym_name);
  pushText(rows, 'availableEquipment', L.availableEquipment, anamnesis.available_equipment);
  pushSelect(
    rows,
    'weeklyAvailability',
    L.weeklyAvailability,
    PUBLIC_WEEKLY_AVAILABILITY_OPTIONS,
    anamnesis.weekly_availability,
  );
  pushSelect(
    rows,
    'availableTrainingTime',
    L.availableTrainingTime,
    PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS,
    anamnesis.available_training_time,
  );
  pushBoolean(rows, 'hadInjury', L.hadInjury, anamnesis.had_injury);
  pushText(rows, 'injuryDescription', L.injuryDescription, anamnesis.injury_description);
  pushBoolean(rows, 'hasPain', L.hasPain, anamnesis.has_pain);
  pushText(rows, 'painLocation', L.painLocation, anamnesis.pain_location);
  pushBoolean(rows, 'painLimitsTraining', L.painLimitsTraining, anamnesis.pain_limits_training);
  pushText(rows, 'limitedExercises', L.limitedExercises, anamnesis.limited_exercises);
  pushBoolean(rows, 'currentlyExercises', L.currentlyExercises, anamnesis.currently_exercises);
  pushText(rows, 'exerciseModalities', L.exerciseModalities, anamnesis.exercise_modalities);
  pushText(rows, 'timeWithoutExercise', L.timeWithoutExercise, anamnesis.time_without_exercise);
  pushText(rows, 'exerciseHistory', L.exerciseHistory, anamnesis.exercise_history);
  pushBoolean(rows, 'enjoysTraining', L.enjoysTraining, anamnesis.enjoys_training);
  pushBoolean(rows, 'usedToEffort', L.usedToEffort, anamnesis.used_to_effort);
  pushBoolean(rows, 'hasNutritionalSupport', L.hasNutritionalSupport, anamnesis.has_nutritional_support);
  pushText(rows, 'dietDetail', L.dietDetail, anamnesis.diet_detail);
  pushBoolean(rows, 'takesMedication', L.takesMedication, anamnesis.takes_medication);
  pushText(rows, 'medications', L.medications, anamnesis.medications);
  pushBoolean(rows, 'hadSurgery', L.hadSurgery, anamnesis.had_surgery);
  pushText(rows, 'surgeries', L.surgeries, anamnesis.surgeries);
  pushBoolean(rows, 'hasChronicDisease', L.hasChronicDisease, anamnesis.has_chronic_disease);
  pushText(rows, 'chronicDiseases', L.chronicDiseases, anamnesis.chronic_diseases);
  pushBoolean(rows, 'hasFamilyHistory', L.hasFamilyHistory, anamnesis.has_family_history);
  pushText(rows, 'familyHistoryDescription', L.familyHistoryDescription, anamnesis.family_history_description);
  pushBoolean(rows, 'hasChildren', L.hasChildren, anamnesis.has_children);
  pushText(rows, 'timeSinceDelivery', L.timeSinceDelivery, anamnesis.time_since_delivery);
  pushBoolean(rows, 'abdominalDiastasis', L.abdominalDiastasis, anamnesis.abdominal_diastasis);
  pushText(rows, 'diastasisTreatment', 'Tratou a diástase?', anamnesis.diastasis_treatment);
  pushSelect(rows, 'mainGoal', L.mainGoal, PUBLIC_MAIN_GOAL_OPTIONS, anamnesis.main_goal);
  pushText(rows, 'goalDetails', L.goalDetails, anamnesis.goal_details);
  pushSelect(rows, 'referralSource', L.referralSource, PUBLIC_REFERRAL_SOURCE_OPTIONS, anamnesis.referral_source);
  pushText(rows, 'additionalInfo', L.additionalInfo, anamnesis.additional_info);
  pushText(rows, 'motivation', L.motivation, anamnesis.motivation);

  return rows;
}

export function buildParqAnswerRows(anamnesis: Anamnesis | null): StudentAnswerRow[] {
  if (!anamnesis) {
    return [];
  }

  return PARQ_QUESTIONS.flatMap((question) => {
    const rows: StudentAnswerRow[] = [{
      key: question.field,
      label: question.label,
      value: anamnesis[question.dbColumn] ?? null,
    }];

    if (question.detailDbColumn && question.detailLabel) {
      rows.push({
        key: question.detailFormField ?? question.detailDbColumn,
        label: question.detailLabel,
        value: textValue(anamnesis[question.detailDbColumn]),
      });
    }

    return rows;
  });
}

export function hasAnsweredAnamnesis(rows: StudentAnswerRow[]): boolean {
  return rows.some((row) => row.value !== null && row.value !== '');
}

export function hasAnsweredParq(rows: StudentAnswerRow[]): boolean {
  return rows.some((row) => typeof row.value === 'boolean');
}
